import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { BaseButton, useT } from '@repo/common';
import HeaderText from '../Common/HeaderText';
import PolicyVersionEditor from './PolicyVersionEditor';
import {
  availableLangs,
  mergePolicyVersions,
} from '../../../pages/Policy/usePolicyVersions';
import { formatPolicyDate, versionPath } from '../../../pages/Policy/policyRegistry';
import {
  deletePolicyVersion,
  listAdminPolicyVersions,
} from '../../../api/policy/policy.api';

// 어드민 정책 문서 버전 관리 — 미리 등록한 버전은 시행일에 자동으로 현재 버전이 된다
const DOCS = [
  { key: 'terms', en: 'Terms of Use', ko: '이용약관' },
  { key: 'privacy', en: 'Privacy Policy', ko: '개인정보처리방침' },
  { key: 'business-terms', en: 'Business Terms', ko: '기업회원 이용약관' },
  { key: 'business-privacy', en: 'Business Privacy', ko: '기업회원 개인정보처리방침' },
  { key: 'refund', en: 'Refund Policy', ko: '환불 정책' },
];

const daysBetween = (fromIso, toIso) =>
  Math.round((Date.parse(toIso) - Date.parse(fromIso)) / 86400000);

const errorText = (error, fallback) =>
  error?.response?.data?.msg || error?.response?.data?.message || fallback;

const StatusChip = ({ tone, children }) => {
  const toneClass = {
    current: 'bg-green-light02 text-green-deep04',
    upcoming: 'bg-functional-warning01 text-text-primary',
    past: 'bg-grayscale-gray01 text-text-placeholder',
  }[tone];
  return (
    <span className={`rounded-full px-[10px] py-[2px] typo-b12Strong whitespace-nowrap ${toneClass}`}>
      {children}
    </span>
  );
};

const AdminPolicies = () => {
  const t = useT();
  const navigate = useNavigate();
  const [policyKey, setPolicyKey] = useState(DOCS[0].key);
  const [remote, setRemote] = useState({ key: null, data: null, failed: false });
  const [tick, setTick] = useState(0);
  const [editing, setEditing] = useState(null);
  const [banner, setBanner] = useState(null);

  useEffect(() => {
    let cancelled = false;
    listAdminPolicyVersions(policyKey)
      .then((data) => !cancelled && setRemote({ key: policyKey, data, failed: false }))
      .catch(() => !cancelled && setRemote({ key: policyKey, data: null, failed: true }));
    return () => {
      cancelled = true;
    };
  }, [policyKey, tick]);

  const loaded = remote.key === policyKey;
  const merged = useMemo(
    () => mergePolicyVersions(policyKey, loaded ? remote.data : null),
    [policyKey, loaded, remote.data],
  );
  const rows = [...merged.all].reverse();

  const selectDoc = (key) => {
    setPolicyKey(key);
    setEditing(null);
    setBanner(null);
  };

  const handleSaved = (saved) => {
    setEditing(null);
    setTick((n) => n + 1);
    setBanner({ tone: 'success', saved });
  };

  const dateLabel = (version) =>
    t(formatPolicyDate(version.effectiveDate, 'en'), formatPolicyDate(version.effectiveDate, 'ko'));

  const handleDelete = async (version) => {
    if (
      !window.confirm(
        t(
          `Delete the version scheduled for ${dateLabel(version)}?`,
          `${dateLabel(version)} 시행 예정 버전을 삭제할까요?`,
        ),
      )
    ) {
      return;
    }
    try {
      await deletePolicyVersion(version.id);
      setBanner(null);
      setTick((n) => n + 1);
    } catch (error) {
      setBanner({
        tone: 'error',
        text: errorText(error, t('Could not delete the version.', '버전을 삭제하지 못했어요.')),
      });
    }
  };

  const writeNotice = (saved) =>
    navigate(
      `/mypage?tab=${encodeURIComponent('Policy Notice')}&noticeDoc=${policyKey}&noticeDate=${saved.effectiveDate}`,
    );

  const statusOf = (version) => {
    if (version.effectiveDate > merged.today) {
      return [
        'upcoming',
        t(
          `Scheduled · D-${daysBetween(merged.today, version.effectiveDate)}`,
          `시행 예정 · D-${daysBetween(merged.today, version.effectiveDate)}`,
        ),
      ];
    }
    if (version === merged.current) return ['current', t('In effect', '현재 시행 중')];
    return ['past', t('Previous', '이전 버전')];
  };

  return (
    <div className='flex flex-col min-w-0 w-full items-start gap-[16px]'>
      <HeaderText label='Policies' />

      <p className='typo-b14 text-text-secondary -mt-[12px]'>
        {t(
          'Register a new version ahead of time. It becomes the live policy on its effective date, and earlier versions stay viewable.',
          '새 버전을 미리 등록해 두면 시행일에 자동으로 바뀌고, 이전 버전은 그대로 열람할 수 있어요. 등록 후 개정 안내 메일을 시행 7일 전까지 보내 주세요.',
        )}
      </p>

      <div className='flex flex-wrap gap-[8px]'>
        {DOCS.map((doc) => (
          <button
            key={doc.key}
            type='button'
            onClick={() => selectDoc(doc.key)}
            className={`rounded-full border px-[14px] py-[6px] typo-b13Strong ${
              policyKey === doc.key
                ? 'border-grayscale-gray09 bg-grayscale-gray09 text-grayscale-gray00'
                : 'border-grayscale-gray02 bg-grayscale-gray00 text-text-secondary hover:bg-grayscale-gray01'
            }`}
          >
            {t(doc.en, doc.ko)}
          </button>
        ))}
      </div>

      {banner?.tone === 'success' && (
        <div className='flex w-full flex-wrap items-center justify-between gap-[8px] rounded-[10px] bg-green-light02 px-[14px] py-[10px] typo-b13 text-green-deep04'>
          <span>
            {t(
              `Saved. It takes effect automatically on ${banner.saved.effectiveDate}.`,
              `저장했어요. ${banner.saved.effectiveDate}에 자동으로 적용돼요.`,
            )}
          </span>
          <BaseButton type='primary' size='s' hideIcon onClick={() => writeNotice(banner.saved)}>
            {t('Write the notice email', '개정 안내 메일 작성하기')}
          </BaseButton>
        </div>
      )}
      {banner?.tone === 'error' && (
        <div className='w-full rounded-[10px] bg-functional-failed01 px-[14px] py-[10px] typo-b13 text-functional-failed03'>
          {banner.text}
        </div>
      )}
      {remote.failed && loaded && (
        <div className='w-full rounded-[10px] bg-functional-failed01 px-[14px] py-[10px] typo-b13 text-functional-failed03'>
          {t(
            'Could not load registered versions. Only built-in versions are shown.',
            '등록된 버전을 불러오지 못했어요. 기본 제공 버전만 보여요.',
          )}
        </div>
      )}

      {editing ? (
        <div className='w-full'>
          <PolicyVersionEditor
            policyKey={policyKey}
            merged={merged}
            editing={editing}
            onCancel={() => setEditing(null)}
            onSaved={handleSaved}
          />
        </div>
      ) : (
        <section className='flex w-full flex-col gap-[12px] rounded-[16px] border border-grayscale-gray02 bg-grayscale-gray00 p-[20px] tabletH:p-[24px]'>
          <div className='flex flex-wrap items-center justify-between gap-[8px]'>
            <h3 className='typo-b16Strong text-text-primary'>{t('Versions', '버전')}</h3>
            <BaseButton
              type='primary'
              size='s'
              hideIcon
              isDisabled={!loaded}
              onClick={() => {
                setBanner(null);
                setEditing({ mode: 'create' });
              }}
            >
              {t('Register a new version', '새 버전 등록')}
            </BaseButton>
          </div>

          {!loaded ? (
            <p className='typo-b14 text-text-placeholder'>{t('Loading…', '불러오는 중…')}</p>
          ) : (
            <ul className='flex flex-col divide-y divide-grayscale-gray02'>
              {rows.map((version) => {
                const [tone, label] = statusOf(version);
                const editable = version.source === 'admin' && tone === 'upcoming';
                return (
                  <li
                    key={version.effectiveDate}
                    className='flex flex-wrap items-center justify-between gap-[8px] py-[12px]'
                  >
                    <div className='flex min-w-0 flex-col gap-[4px]'>
                      <div className='flex flex-wrap items-center gap-[8px]'>
                        <span className='typo-b14Strong text-text-primary'>{dateLabel(version)}</span>
                        <StatusChip tone={tone}>{label}</StatusChip>
                        {availableLangs(version).map((code) => (
                          <span
                            key={code}
                            className='rounded-[4px] border border-grayscale-gray02 px-[6px] typo-b11Strong text-text-secondary'
                          >
                            {code.toUpperCase()}
                          </span>
                        ))}
                      </div>
                      <span className='typo-b12 text-text-placeholder'>
                        {availableLangs(version)
                          .map((code) => version.languages[code].title)
                          .join(' / ')}{' '}
                        ·{' '}
                        {version.source === 'admin'
                          ? t('Registered by admin', '어드민 등록')
                          : t('Built in', '기본 제공')}
                      </span>
                    </div>
                    <div className='flex flex-wrap gap-[4px]'>
                      <a
                        href={versionPath(policyKey, version.effectiveDate)}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='rounded-[6px] px-[10px] py-[6px] typo-b13Strong text-text-secondary hover:bg-grayscale-gray01'
                      >
                        {t('View', '보기')}
                      </a>
                      {editable && (
                        <>
                          <button
                            type='button'
                            onClick={() => {
                              setBanner(null);
                              setEditing({ mode: 'edit', version });
                            }}
                            className='rounded-[6px] px-[10px] py-[6px] typo-b13Strong text-text-secondary hover:bg-grayscale-gray01'
                          >
                            {t('Edit', '수정')}
                          </button>
                          <button
                            type='button'
                            onClick={() => handleDelete(version)}
                            className='rounded-[6px] px-[10px] py-[6px] typo-b13Strong text-functional-failed03 hover:bg-functional-failed01'
                          >
                            {t('Delete', '삭제')}
                          </button>
                          <button
                            type='button'
                            onClick={() => writeNotice(version)}
                            className='rounded-[6px] px-[10px] py-[6px] typo-b13Strong text-green-deep04 hover:bg-green-light02'
                          >
                            {t('Notice email', '안내 메일')}
                          </button>
                        </>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          )}
        </section>
      )}
    </div>
  );
};

export default AdminPolicies;
